/**
 * Attendee List Component
 * Displays event attendees grouped by RSVP status
 */

import { Link } from 'react-router-dom'
import {
  Box,
  Typography,
  Avatar,
  AvatarGroup,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  Tabs,
  Tab,
  Skeleton,
  Paper,
} from '@mui/material'
import {
  CheckCircle,
  HelpOutline,
  Cancel,
} from '@mui/icons-material'
import { useState } from 'react'
import {
  EventAttendeeDto,
  RsvpStatus,
  getRsvpStatusLabel,
} from '@api/types/calendarEvent'
import { useEventAttendees } from '../hooks/useCalendarEvents'

interface AttendeeListProps {
  eventId: string
  attendingCount: number
  tentativeCount: number
  notAttendingCount: number
  variant?: 'full' | 'summary'
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      sx={{ pt: 2 }}
    >
      {value === index && children}
    </Box>
  )
}

/**
 * Get icon for RSVP status
 */
function getRsvpIcon(status: RsvpStatus) {
  switch (status) {
    case RsvpStatus.Attending:
      return <CheckCircle color="success" fontSize="small" />
    case RsvpStatus.Tentative:
      return <HelpOutline color="warning" fontSize="small" />
    case RsvpStatus.NotAttending:
      return <Cancel color="error" fontSize="small" />
    default:
      return null
  }
}

/**
 * Attendee list item
 */
function AttendeeItem({ attendee }: { attendee: EventAttendeeDto }) {
  return (
    <ListItem
      component={Link}
      to={`/profile/${attendee.userId}`}
      sx={{
        textDecoration: 'none',
        color: 'inherit',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      <ListItemAvatar>
        <Avatar src={attendee.profileImageUrl}>
          {attendee.displayName.charAt(0)}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={attendee.displayName}
        secondary={attendee.note}
        primaryTypographyProps={{
          variant: 'body2',
          fontWeight: 500,
        }}
        secondaryTypographyProps={{
          variant: 'caption',
          sx: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
      />
      {getRsvpIcon(attendee.status)}
    </ListItem>
  )
}

/**
 * Summary view showing avatars grouped
 */
function AttendeesSummary({
  eventId,
  attendingCount,
  tentativeCount,
}: Pick<AttendeeListProps, 'eventId' | 'attendingCount' | 'tentativeCount'>) {
  const { data, isLoading } = useEventAttendees(eventId, {
    pageSize: 10,
    status: RsvpStatus.Attending,
  })

  const attendees = (data as any)?.attendees || []

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AvatarGroup max={5}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="circular" width={32} height={32} />
          ))}
        </AvatarGroup>
      </Box>
    )
  }

  if (attendees.length === 0 && attendingCount === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No one has RSVP'd yet
      </Typography>
    )
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AvatarGroup max={5}>
          {attendees.map((attendee) => (
            <Avatar
              key={attendee.userId}
              src={attendee.profileImageUrl}
              sx={{ width: 32, height: 32 }}
            >
              {attendee.displayName.charAt(0)}
            </Avatar>
          ))}
        </AvatarGroup>
        {attendingCount > 0 && (
          <Chip
            icon={<CheckCircle fontSize="small" />}
            label={`${attendingCount} attending`}
            color="success"
            variant="outlined"
            size="small"
          />
        )}
      </Box>
      {tentativeCount > 0 && (
        <Chip
          icon={<HelpOutline fontSize="small" />}
          label={`${tentativeCount} maybe`}
          color="warning"
          variant="outlined"
          size="small"
        />
      )}
    </Box>
  )
}

/**
 * Full view with tabs for each status
 */
function AttendeesFullView({
  eventId,
  attendingCount,
  tentativeCount,
  notAttendingCount,
}: Omit<AttendeeListProps, 'variant'>) {
  const [tab, setTab] = useState(0)

  // Determine which status to fetch based on tab
  const statusMap: Record<number, RsvpStatus> = {
    0: RsvpStatus.Attending,
    1: RsvpStatus.Tentative,
    2: RsvpStatus.NotAttending,
  }

  const { data, isLoading } = useEventAttendees(eventId, {
    status: statusMap[tab],
  })

  const attendees = (data as any)?.attendees || []

  return (
    <Paper variant="outlined">
      <Tabs
        value={tab}
        onChange={(_, newValue) => setTab(newValue)}
        variant="fullWidth"
      >
        <Tab
          label={`Attending (${attendingCount})`}
          icon={<CheckCircle color="success" fontSize="small" />}
          iconPosition="start"
        />
        <Tab
          label={`Maybe (${tentativeCount})`}
          icon={<HelpOutline color="warning" fontSize="small" />}
          iconPosition="start"
        />
        <Tab
          label={`Not Going (${notAttendingCount})`}
          icon={<Cancel color="error" fontSize="small" />}
          iconPosition="start"
        />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <AttendeeListContent attendees={attendees} isLoading={isLoading} emptyMessage="No one is attending yet" />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <AttendeeListContent attendees={attendees} isLoading={isLoading} emptyMessage="No maybes yet" />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <AttendeeListContent attendees={attendees} isLoading={isLoading} emptyMessage="No declines yet" />
      </TabPanel>
    </Paper>
  )
}

/**
 * List content for a single status tab
 */
function AttendeeListContent({
  attendees,
  isLoading,
  emptyMessage,
}: {
  attendees: EventAttendeeDto[]
  isLoading: boolean
  emptyMessage: string
}) {
  if (isLoading) {
    return (
      <List>
        {[1, 2, 3].map((i) => (
          <ListItem key={i}>
            <ListItemAvatar>
              <Skeleton variant="circular" width={40} height={40} />
            </ListItemAvatar>
            <ListItemText
              primary={<Skeleton variant="text" width="60%" />}
              secondary={<Skeleton variant="text" width="40%" />}
            />
          </ListItem>
        ))}
      </List>
    )
  }

  if (attendees.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    )
  }

  return (
    <List disablePadding>
      {attendees.map((attendee) => (
        <AttendeeItem key={attendee.userId} attendee={attendee} />
      ))}
    </List>
  )
}

/**
 * Main Attendee List component
 */
export function AttendeeList({
  eventId,
  attendingCount,
  tentativeCount,
  notAttendingCount,
  variant = 'summary',
}: AttendeeListProps) {
  if (variant === 'summary') {
    return (
      <AttendeesSummary
        eventId={eventId}
        attendingCount={attendingCount}
        tentativeCount={tentativeCount}
      />
    )
  }

  return (
    <AttendeesFullView
      eventId={eventId}
      attendingCount={attendingCount}
      tentativeCount={tentativeCount}
      notAttendingCount={notAttendingCount}
    />
  )
}

export default AttendeeList
